import { createContext, useCallback, useContext, useMemo, useRef, useState, type ReactNode } from 'react';
import { Icon, type IconName } from '@serota/icons/web';
import { cx } from '../lib';

export interface AlertProps {
  tone: 'info' | 'ok' | 'warn' | 'crit';
  title?: ReactNode;
  children?: ReactNode;
  icon?: IconName;
  action?: ReactNode;
  style?: React.CSSProperties;
}

const DEFAULT_ICON: Record<AlertProps['tone'], IconName> = { info: 'rota', ok: 'check', warn: 'clock', crit: 'uyari' };

/** İkon + kalın ilk cümle + açıklama. Hata mesajı suçlamaz, sonraki adımı söyler. */
export function Alert({ tone, title, children, icon, action, style }: AlertProps) {
  return (
    <div className={cx('sr-alert', `sr-alert--${tone}`)} role={tone === 'crit' ? 'alert' : 'status'} style={style}>
      <Icon name={icon ?? DEFAULT_ICON[tone]} />
      <div style={{ flex: 1 }}>
        {title ? <b>{title}</b> : null} {children}
      </div>
      {action}
    </div>
  );
}

interface ToastItem {
  id: number;
  text: ReactNode;
  icon?: IconName;
  action?: { label: string; onClick: () => void };
  duration: number;
}

interface ToastApi {
  show: (
    text: ReactNode,
    opts?: { icon?: IconName; action?: { label: string; onClick: () => void }; duration?: number },
  ) => void;
}

const ToastCtx = createContext<ToastApi>({ show: () => undefined });

/** Toast: `surface-inverse` üzerinde kısa onay, 4 sn; "Geri al" varsa 10 sn. */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([]);
  const seq = useRef(0);
  const remove = useCallback((id: number) => setItems((xs) => xs.filter((x) => x.id !== id)), []);
  const show = useCallback<ToastApi['show']>(
    (text, opts) => {
      const id = ++seq.current;
      const duration = opts?.duration ?? (opts?.action ? 10000 : 4000);
      setItems((xs) => [...xs, { id, text, icon: opts?.icon ?? 'check', action: opts?.action, duration }]);
      setTimeout(() => remove(id), duration);
    },
    [remove],
  );
  const api = useMemo(() => ({ show }), [show]);
  return (
    <ToastCtx.Provider value={api}>
      {children}
      <div className="sr-toast-stack" aria-live="polite">
        {items.map((t) => (
          <div key={t.id} className="sr-toast">
            {t.icon ? <Icon name={t.icon} /> : null}
            <span style={{ flex: 1 }}>{t.text}</span>
            {t.action ? (
              <button
                type="button"
                className="sr-toast__action"
                onClick={() => {
                  t.action?.onClick();
                  remove(t.id);
                }}
              >
                {t.action.label}
              </button>
            ) : null}
          </div>
        ))}
      </div>
    </ToastCtx.Provider>
  );
}

export function useToast(): ToastApi {
  return useContext(ToastCtx);
}

/** Statik toast (galeri/önizleme). */
export function Toast({ children, icon = 'check', action }: { children: ReactNode; icon?: IconName; action?: string }) {
  return (
    <div className="sr-toast" style={{ animation: 'none' }}>
      <Icon name={icon} />
      <span style={{ flex: 1 }}>{children}</span>
      {action ? <b style={{ textDecoration: 'underline' }}>{action}</b> : null}
    </div>
  );
}
